<script setup lang="ts">
import { computed } from "vue";
import { storeToRefs } from "pinia";
import { useResumeStore } from "~/stores/useResumeStore";
import aboutBackgroundImage from "./bg02-right.jpg";
import { formatAboutParagraph } from "~/helpers/text";
import { calculateYearsOfExperience } from "~/helpers/date";

const resumeStore = useResumeStore();
const { aboutMe, experiences } = storeToRefs(resumeStore);

const yearsOfExperience = computed<number>(() => {
  const startDates = experiences.value.map(
    (experience) => experience.startDate
  );

  return calculateYearsOfExperience(startDates) ?? 0;
});

const aboutHeadline = computed(() => aboutMe.value?.headline);
const aboutParagraphs = computed(() => aboutMe.value?.paragraphs);
const formattedAboutParagraphs = computed(() => {
  return (aboutParagraphs.value ?? []).map((paragraph) =>
    formatAboutParagraph(paragraph, yearsOfExperience.value)
  );
});
</script>

<template>
  <section
    v-if="aboutMe"
    class="relative lg:min-h-[calc(100vh-var(--main-nav-height))] overflow-x bg-white dark:bg-primary-950 left-1/2 -translate-x-1/2 w-screen"
  >
    <div
      class="hidden lg:block absolute inset-0 bg-cover bg-right-center dark:opacity-60"
      :style="{
        backgroundImage: `url(${aboutBackgroundImage})`,
      }"
    >
      <div
        class="absolute inset-0 bg-gradient-to-r from-white/95 via-white/80 to-white/10 dark:from-primary-950/95 dark:via-primary-950/75 dark:to-primary-950/10"
      />
      <div
        class="absolute inset-0 bg-gradient-to-t from-white/85 via-white/25 to-transparent dark:from-primary-950/85 dark:via-primary-950/20 dark:to-transparent"
      />
      <div
        class="pointer-events-none absolute inset-x-0 top-0 h-12 dark:h-8 bg-gradient-to-b from-primary-900/10 to-transparent dark:from-primary-950/55 z-10"
      />
      <div
        class="pointer-events-none absolute inset-x-0 bottom-0 h-12 dark:h-8 bg-gradient-to-t from-primary-900/10 to-transparent dark:from-primary-950/55"
      />
    </div>

    <div
      class="relative z-10 lg:min-h-[100vh] lg:container lg:mx-auto lg:grid lg:grid-cols-12 px-4 lg:px-0"
    >
      <div class="col-span-11 col-start-2 col-end-12 flex items-center">
        <div class="w-full lg:w-1/2 space-y-6">
          <UiTitle
            level="3"
            class="text-lg font-bold text-primary-700 dark:text-primary-300 mb-4"
          >
            About Me
          </UiTitle>
          <UiTitle
            level="4"
            class="text-3xl lg:text-6xl font-semibold leading-tight mb-12"
          >
            {{ aboutHeadline }}
          </UiTitle>

          <UiParagraph
            v-for="paragraph in formattedAboutParagraphs"
            :key="paragraph"
            class="text-base lg:text-lg leading-8"
          >
            {{ paragraph }}
          </UiParagraph>
        </div>
      </div>
    </div>
  </section>
</template>
